import { ConfigManager } from '@unidev-hub/config';
import { Logger } from '@unidev-hub/logger';
import {
  CorrelationIdOptions,
  MetricsOptions,
  RequestLoggerOptions,
  ErrorHandlingOptions,
  ResponseFormatOptions,
  RateLimitOptions,
  AuthOptions,
  ValidationOptions,
  UploadOptions
} from './types';

/**
 * Unified middleware configuration type
 */
export interface MiddlewareConfig {
  auth?: AuthOptions;
  uploads?: UploadOptions;
  validation?: ValidationOptions;
  correlation?: CorrelationIdOptions;
  metrics?: MetricsOptions;
  logging?: RequestLoggerOptions;
  errors?: ErrorHandlingOptions;
  responses?: ResponseFormatOptions;
  rateLimiting?: RateLimitOptions;
}

/**
 * Default configuration for middleware
 */
let config: MiddlewareConfig = {
  auth: {
    required: true,
    type: 'jwt'
  },
  uploads: {
    maxSize: 10 * 1024 * 1024, // 10MB default
    allowedMimeTypes: [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ]
  },
  validation: {
    useHttpError: true,
    abortEarly: false,
    stripUnknown: true,
    allowUnknown: false
  },
  correlation: {
    header: 'X-Correlation-ID',
    propertyName: 'correlationId',
    generateId: true
  },
  logging: {
    level: 'info',
    includeBody: false,
    includeHeaders: false,
    skip: ['/health', '/metrics']
  },
  errors: {
    includeStack: process.env.NODE_ENV !== 'production',
    logErrors: true
  },
  responses: {
    wrapSuccess: true,
    includeRequestId: true
  },
  rateLimiting: {
    windowMs: 15 * 60 * 1000, // 15 minutes
    max: 100 // 100 requests per windowMs
  }
};

// Store logger instance
let logger: Logger;

/**
 * Initialize middleware with configuration
 * @param configManager Configuration manager
 * @param loggerInstance Logger instance
 */
export function initializeMiddleware(
  configManager: ConfigManager,
  loggerInstance: Logger
): void {
  logger = loggerInstance;

  // Load configuration from config manager
  config = {
    // Auth config
    auth: {
      required: configManager.get<boolean>('auth.required', true),
      type: configManager.get<'jwt' | 'azure' | 'none' | 'custom'>('auth.type', 'jwt'),
      jwt: {
        secret: configManager.get<string>('auth.jwt.secret'),
        publicKey: configManager.get<string>('auth.jwt.publicKey'),
        issuer: configManager.get<string>('auth.jwt.issuer'),
        audience: configManager.get<string>('auth.jwt.audience')
      },
      azure: {
        tenantId: configManager.get<string>('auth.azure.tenantId'),
        clientId: configManager.get<string>('auth.azure.clientId'),
        clientSecret: configManager.get<string>('auth.azure.clientSecret'),
        apiScope: configManager.get<string>('auth.azure.apiScope')
      }
    },

    // Uploads config
    uploads: {
      maxSize: configManager.get<number>('uploads.maxSize', config.uploads?.maxSize || 10 * 1024 * 1024),
      allowedMimeTypes: configManager.get<string[]>('uploads.allowedMimeTypes', config.uploads?.allowedMimeTypes || []),
      destination: configManager.get<string>('uploads.destination')
    },

    // Validation config
    validation: {
      useHttpError: configManager.get<boolean>('validation.useHttpError', true),
      abortEarly: configManager.get<boolean>('validation.abortEarly', false),
      stripUnknown: configManager.get<boolean>('validation.stripUnknown', true),
      allowUnknown: configManager.get<boolean>('validation.allowUnknown', false)
    },

    // Correlation ID config
    correlation: {
      header: configManager.get<string>('correlation.header', 'X-Correlation-ID'),
      propertyName: configManager.get<string>('correlation.propertyName', 'correlationId'),
      generateId: configManager.get<boolean>('correlation.generateId', true)
    },

    // Metrics config
    metrics: {
      prefix: configManager.get<string>('metrics.prefix', ''),
      defaultMetrics: configManager.get<boolean>('metrics.defaultMetrics', true),
      endpoint: configManager.get<string>('metrics.endpoint', '/metrics'),
      defaultLabels: configManager.get<Record<string, string>>('metrics.defaultLabels')
    },

    // Logging config
    logging: {
      level: configManager.get<'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal'>('logging.level', 'info'),
      includeBody: configManager.get<boolean>('logging.includeBody', false),
      includeHeaders: configManager.get<boolean>('logging.includeHeaders', false),
      skip: configManager.get<string[]>('logging.skip', ['/health', '/metrics'])
    },

    // Error handling config
    errors: {
      includeStack: configManager.get<boolean>('errors.includeStack', process.env.NODE_ENV !== 'production'),
      logErrors: configManager.get<boolean>('errors.logErrors', true),
      defaultMessage: configManager.get<string>('errors.defaultMessage', 'An unexpected error occurred'),
      defaultCode: configManager.get<string>('errors.defaultCode', 'INTERNAL_ERROR')
    },

    // Response formatting config
    responses: {
      wrapSuccess: configManager.get<boolean>('responses.wrapSuccess', true),
      includeRequestId: configManager.get<boolean>('responses.includeRequestId', true),
      defaultSuccessMessage: configManager.get<string>('responses.defaultSuccessMessage', 'Operation successful'),
      defaultErrorMessage: configManager.get<string>('responses.defaultErrorMessage', 'Operation failed')
    },

    // Rate limiting config
    rateLimiting: {
      windowMs: configManager.get<number>('rateLimiting.windowMs', 15 * 60 * 1000),
      max: configManager.get<number>('rateLimiting.max', 100),
      standardHeaders: configManager.get<boolean>('rateLimiting.standardHeaders', true),
      message: configManager.get<string>('rateLimiting.message', 'Too many requests, please try again later.')
    }
  };

  logger.debug('Middleware configuration initialized', { config });
}

/**
 * Update middleware configuration
 * @param newConfig New configuration to merge with existing config
 */
export function configMiddleware(newConfig: Partial<MiddlewareConfig>): void {
  // Deep merge the new config with the existing config
  config = {
    ...config,
    ...newConfig,
    auth: {
      ...config.auth,
      ...newConfig.auth,
      jwt: {
        ...config.auth?.jwt,
        ...newConfig.auth?.jwt
      },
      azure: {
        ...config.auth?.azure,
        ...newConfig.auth?.azure
      }
    },
    uploads: {
      ...config.uploads,
      ...newConfig.uploads
    },
    validation: {
      ...config.validation,
      ...newConfig.validation
    },
    correlation: {
      ...config.correlation,
      ...newConfig.correlation
    },
    metrics: {
      ...config.metrics,
      ...newConfig.metrics
    },
    logging: {
      ...config.logging,
      ...newConfig.logging
    },
    errors: {
      ...config.errors,
      ...newConfig.errors
    },
    responses: {
      ...config.responses,
      ...newConfig.responses
    },
    rateLimiting: {
      ...config.rateLimiting,
      ...newConfig.rateLimiting
    }
  };

  if (logger) {
    logger.debug('Middleware configuration updated', { config });
  }
}

/**
 * Get current middleware configuration
 */
export function getConfig(): MiddlewareConfig {
  return { ...config };
}
